/**
 * WHAT IS LEFT OF THE TECHNICIAN'S TESTS.
 *
 * There used to be a step in the technician's walk that made them record
 * each test they ran and tap Passed or Found-the-fault against it, with an
 * AI planner to propose the list. Anthony, 21 September: "it's confusing
 * and redundant." It was removed whole — the cards, the verdicts, the
 * planner, and the rule that would not let anybody past the step until at
 * least one verdict was marked. What the technician found is now one box
 * of their own words. It was stamped onto the job's notes behind FOUND_TAG
 * until 30 September, and since 0188 it is the diagnostic card, a column
 * of its own with a screen of its own (pages/diagnosis.ts). The tag is
 * still read, as the fallback in diagnosisOf(), and no longer written.
 *
 * REMOVED, NOT ERASED. Two stores still hold real work from before:
 *
 *   - the `job_tests` table (migration 0082), left in place with its rows
 *     and its RLS intact. Nothing reads it any more. It is not dropped
 *     because "for now" was the instruction and a dropped table takes its
 *     rows with it.
 *   - the older `[tech tests]` JSON fence inside jobs.notes, from before
 *     0082 moved them out. Those fences are still sitting in the notes of
 *     old jobs, which is the whole reason this file did not disappear:
 *     humanNotes and fencedTests are what stop a wall of JSON braces being
 *     shown to somebody as a diagnosis.
 *
 * To bring the step back, `git show <this commit>^:src/data/tests.ts` and
 * the same path on src/pages/tech.ts.
 */

const OPEN = "[tech tests]";
const SHUT = "[/tech tests]";

/** How the technician's summary is marked in the notes, for the desk. */
export const FOUND_TAG = "[what the tech found]";

/**
 * Has the technician already said what they found on this job?
 *
 * This replaced counting rows in job_tests. It is deliberately a question
 * about the job's own record rather than anything a screen remembers, so a
 * technician who wrote it up this morning is not asked again after lunch —
 * the same rule the rest of the walk follows.
 */
export const hasFound = (notes: string | null): boolean =>
  String(notes ?? "").includes(FOUND_TAG);

/**
 * THE DIAGNOSTIC CARD, WHEREVER IT IS WRITTEN.
 *
 * Since 0188 the card is jobs.diagnosis. Before that it was the last
 * tagged line in the notes, and the migration copied those across, but a
 * job whose notes were tagged after the copy (or a shop whose database has
 * not had 0188 yet) still only has the tag. So the column wins and the tag
 * is the fallback. Every reader goes through here so the walk, the job
 * card and the drafter cannot disagree about whether anything was found.
 */
export function diagnosisOf(job: { diagnosis?: string | null; notes: string | null }): string {
  const own = String(job.diagnosis ?? "").trim();
  if (own) return own;
  const all = String(job.notes ?? "");
  const i = all.lastIndexOf(FOUND_TAG);
  if (i < 0) return "";
  const rest = all.slice(i + FOUND_TAG.length);
  const end = rest.indexOf("\n\n");
  return (end < 0 ? rest : rest.slice(0, end)).trim();
}

/** Has anybody written the diagnostic card for this job yet? */
export const hasDiagnosis = (job: { diagnosis?: string | null; notes: string | null }): boolean =>
  diagnosisOf(job) !== "";

/* ── the old fence, which is now only ever read ───────────────────────── */

export function fencedTests(notes: string | null): string {
  const all = String(notes ?? "");
  const a = all.indexOf(OPEN);
  const b = all.indexOf(SHUT);
  return a >= 0 && b > a ? all.slice(a, b + SHUT.length) : "";
}

/** Everything a person wrote, with the machinery taken out. */
export function humanNotes(notes: string | null): string {
  const all = String(notes ?? "");
  const a = all.indexOf(OPEN);
  const b = all.indexOf(SHUT);
  if (a < 0 || b < a) return all;
  return (all.slice(0, a) + all.slice(b + SHUT.length)).trim();
}
